<template>
  <mi-tab-panel class="search-results row no-wrap q-gutter-md q-pa-none">
    <!-- Search results -->
    <div class="col full-height">
      <search-results-table
        v-model="searchStringElements"
        :header-color="currentResultsType.color"
        :items="filteredElements"
        :show-no-data-found="isSearchPerformed && !filteredElements.length"
      >
        <template #default="{ item }">
          <mi-list-item
            :key="item.id"
            :class="{
              'search-results-table__item--selected': currentPreviewElementsIds.includes(item.id),
              [`search-results-table__item--${ currentResultsType.color }`]: true
            }"
            class="items-center"
            clickable
            :disable="isComponentLoading"
            @click="toggleElementInPreview(item)"
          >
            <search-results-item-title :code-id="item.id2" :name="item.name"></search-results-item-title>
            <mi-icon class="q-ml-sm" name="caret-right" size="8px"></mi-icon>
          </mi-list-item>
        </template>
      </search-results-table>
    </div>

    <!-- Preview items -->
    <div class="col full-height">
      <search-results-table
        ref="previewItemsTable"
        v-model="searchStringPreviewElements"
        :header-color="currentPreviewElements.length ? currentResultsType.color : ''"
        :items="filteredPreviewElements"
        :disable="!currentPreviewElements.length"
        :show-no-data-found="!!searchStringPreviewElements?.length && !filteredPreviewElements.length"
      >
        <template #default="{ item }">
          <search-results-group
            :key="item.id"
            :item="item"
            :color="currentResultsType.color"
            :selected-elements-ids="selectedElementsIds"
            :handle-item-click="handleItemClick"
            :handle-sub-item-click="handleSubItemClick"
            :total-sub-items-count-map="totalSubItemsCountMap"
            :all-sub-items-map="allSubItemsMap"
            :on-toggle-full-selection="handleToggleFullSelection"
            :show-selection-toggle="isChoicesTab"
          >
          </search-results-group>
        </template>
      </search-results-table>
    </div>
  </mi-tab-panel>
</template>

<script>
  import cloneDeep from 'lodash.clonedeep'
  import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'

  import { SEARCH_RESULT_TYPES, ELEMENT_STATES } from '@/constants'
  import { TOGGLE_ELEMENT_STATE, TOGGLE_SUB_ELEMENT_STATE } from '@/store/modules/search/mutationTypes'
  import { getOption } from '@/api'

  import { filterElementByString, filterElementsListByString, getElementTypes } from '@/utils/filterListByString'
  import SearchResultsTable from './SearchResultsTable.vue'
  import SearchResultsGroup from './SearchResultsGroup.vue'
  import SearchResultsItemTitle from './SearchResultsItemTitle.vue'

  export default {
    name: 'SearchResults',
    components: {
      SearchResultsGroup,
      SearchResultsTable,
      SearchResultsItemTitle
    },
    data: () => ({
      ELEMENT_STATES,
      searchStringElements: '',
      searchStringPreviewElements: '',
      isComponentLoading: '',
      partialSelectionSnapshots: {},
      fullOptionCache: {}
    }),
    computed: {
      ...mapState('search', ['selectedElementsIds']),
      ...mapState('search', ['areElementsLoading', 'isSearchPerformed', 'selectedResultType']),
      ...mapState('product-model', ['selectedSearchProductModel']),
      ...mapGetters('product-model', ['selectedProductModel', 'hasSelectedSearchProductModel']),
      ...mapGetters('search', ['currentPreviewElements', 'currentPreviewElementsIds', 'currentResultsType']),

      subElementsType() {
        return getElementTypes(this.selectedResultType).subtype
      },
      filteredElements() {
        return Object.freeze(this.currentResultsType?.items.filter(
          element => filterElementByString({ element, searchString: this.searchStringElements })
        )) || []
      },
      filteredPreviewElements() {
        const searchString = this.searchStringPreviewElements
        const previewElements = cloneDeep(this.currentPreviewElements)
        this.componentLoadingState('completed')

        return filterElementsListByString(
          {
            elementsList: previewElements,
            elementType: this.selectedResultType,
            searchString
          }
        )
      },
      totalSubItemsCountMap() {
        const map = {}
        const { type, subtype } = getElementTypes(this.selectedResultType)

        if (this.isChoicesTab) {
          Object.values(this.fullOptionCache).forEach(option => {
            // eslint-disable-next-line security/detect-object-injection
            map[option.id] = option[subtype]?.length || 0
          })
          return map
        }

        // eslint-disable-next-line security/detect-object-injection
        const items = this.$store.state.search.elements?.structure?.[type]?.items || []
        items.forEach(element => {
          // eslint-disable-next-line security/detect-object-injection
          map[element.id] = element[subtype]?.length || 0
        })
        return map
      },
      allSubItemsMap() {
        const map = {}
        const { type, subtype } = getElementTypes(this.selectedResultType)

        if (this.isChoicesTab) {
          Object.values(this.fullOptionCache).forEach(option => {
            // eslint-disable-next-line security/detect-object-injection
            map[option.id] = option[subtype] || []
          })
          return map
        }

        // eslint-disable-next-line security/detect-object-injection
        const items = this.$store.state.search.elements?.structure?.[type]?.items || []
        items.forEach(element => {
          // eslint-disable-next-line security/detect-object-injection
          map[element.id] = element[subtype] || []
        })
        return map
      },
      isChoicesTab() {
        return this.selectedResultType === SEARCH_RESULT_TYPES.CHOICES
      }
    },
    watch: {
      currentPreviewElements: {
        async handler(elements) {
          this.componentLoadingState('completed')

          if (this.isChoicesTab) {
            const fetchPromises = elements
              .filter(el => !this.fullOptionCache[el.id])
              .map(el => this.fetchFullOption(el.id))
            await Promise.all(fetchPromises)
          }
        },
        deep: true
      },
      areElementsLoading(current) {
        if (current) {
          this.searchStringElements = ''
          this.searchStringPreviewElements = ''
          this.fullOptionCache = {}
        }
      },
      currentPreviewElementsIds(current, initial) {
        if (current > initial) {
          this.$refs.previewItemsTable?.refreshVirtualScroll?.()
        }
      }
    },
    methods: {
      ...mapActions('search', ['getComponentWithVariants', 'getOptionWithChoices']),
      ...mapMutations('search', { TOGGLE_ELEMENT_STATE, TOGGLE_SUB_ELEMENT_STATE }),
      componentLoadingState(state = '') {
        this.isComponentLoading = (state === 'loading')
      },
      toggleElementInPreview(element = {}) {
        const isInPreview = this.currentPreviewElementsIds.includes(element.id)
        this.componentLoadingState('loading')

        switch (this.selectedResultType) {
          case SEARCH_RESULT_TYPES.OPTIONS:
            this.getOptionWithChoices({ option: element, isInPreview })

            break

          case SEARCH_RESULT_TYPES.CHOICES:
            this.TOGGLE_SUB_ELEMENT_STATE({
              subElement: element,
              subElementsType: SEARCH_RESULT_TYPES.CHOICES,
              subElementState: ELEMENT_STATES.IN_PREVIEW,
              elementsType: SEARCH_RESULT_TYPES.OPTIONS,
              isChosen: isInPreview
            })
            break

          case SEARCH_RESULT_TYPES.COMPONENTS:
            this.getComponentWithVariants({ component: element, isInPreview })
            break

          case SEARCH_RESULT_TYPES.COMPONENT_VARIANTS:
            this.TOGGLE_SUB_ELEMENT_STATE({
              subElement: element,
              subElementsType: SEARCH_RESULT_TYPES.COMPONENT_VARIANTS,
              subElementState: ELEMENT_STATES.IN_PREVIEW,
              elementsType: SEARCH_RESULT_TYPES.COMPONENTS,
              isChosen: isInPreview
            })
            break

          default:
            break
        }
      },
      handleItemClick(elementTypes, { item = {}, isChosen = false } = {}) {
        this.TOGGLE_ELEMENT_STATE({
          element: item,
          elementsType: elementTypes.item,
          elementState: ELEMENT_STATES.SELECTED,
          subElementsType: elementTypes.subItem,
          isChosen
        })
      },

      handleSubItemClick(elementTypes, { subItem = {}, isChosen = false } = {}) {
        this.TOGGLE_SUB_ELEMENT_STATE({
          subElement: subItem,
          subElementsType: elementTypes.subItem,
          subElementState: ELEMENT_STATES.SELECTED,
          elementsType: elementTypes.item,
          isChosen
        })
      },

      handleToggleFullSelection({ item, elementTypes, isFull }) {
        const currentSelectedIds = this.selectedElementsIds[elementTypes.item]?.get(item.id) || []
        const allSubItems = this.allSubItemsMap[item.id] || []

        if (isFull) {
          if (currentSelectedIds.length < allSubItems.length) {
            this.partialSelectionSnapshots[item.id] = [...currentSelectedIds]
          }

          allSubItems.forEach(subItem => {
            const isSelected = currentSelectedIds.includes(subItem.id)
            if (!isSelected) {
              this.TOGGLE_SUB_ELEMENT_STATE({
                subElement: subItem,
                subElementsType: elementTypes.subItem,
                subElementState: ELEMENT_STATES.IN_PREVIEW,
                elementsType: elementTypes.item,
                isChosen: false
              })
              this.TOGGLE_SUB_ELEMENT_STATE({
                subElement: subItem,
                subElementsType: elementTypes.subItem,
                subElementState: ELEMENT_STATES.SELECTED,
                elementsType: elementTypes.item,
                isChosen: false
              })
            }
          })
        }
        else {
          const snapshot = this.partialSelectionSnapshots[item.id] || []

          allSubItems.forEach(subItem => {
            const isSelected = currentSelectedIds.includes(subItem.id)
            const shouldBeSelected = snapshot.includes(subItem.id)
            if (isSelected && !shouldBeSelected) {
              this.TOGGLE_SUB_ELEMENT_STATE({
                subElement: subItem,
                subElementsType: elementTypes.subItem,
                subElementState: ELEMENT_STATES.IN_PREVIEW,
                elementsType: elementTypes.item,
                isChosen: true
              })
              this.TOGGLE_SUB_ELEMENT_STATE({
                subElement: subItem,
                subElementsType: elementTypes.subItem,
                subElementState: ELEMENT_STATES.SELECTED,
                elementsType: elementTypes.item,
                isChosen: true
              })
            }
          })
        }

        this.$nextTick(() => {
          this.$refs.previewItemsTable?.refreshVirtualScroll?.()
        })
      },

      async fetchFullOption(optionId) {
        // eslint-disable-next-line security/detect-object-injection
        if (this.fullOptionCache[optionId]) return

        const pmModel = this.selectedSearchProductModel || {}
        const option = await getOption({
          id: optionId,
          pmIdentifier: pmModel.encodedBusinessName || pmModel.id,
          pmType: pmModel.productModelType
        })

        this.fullOptionCache = { ...this.fullOptionCache, [optionId]: option }
      }

    }
  }
</script>

<style lang="scss" scoped>
  .search-results {
    width: auto;
  }

  .search-results-table__item--selected.search-results-table__item--yellow {
    background-color: lighten($mi-yellow-400, 35%);
  }

  .search-results-table__item--selected.search-results-table__item--green {
    background-color: lighten($mi-green-200, 9%);
  }
</style>
